import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { siteContent } from "../mock/rosablu.mock";
import { Button } from "./ui/button";
import { MessageCircle, Menu, X } from "lucide-react";

const routeMap = {
  "chi-siamo": "/chi-siamo",
  suites: "/appartamento",
  "val-di-sole": "/attivita",
  faq: "/faq",
  contatti: "/contatti",
};

const WhatsappBubble = () => {
  const { lang } = useLanguage();

  const baseUrl = "https://wa.me/393895151393";
  const message =
    lang === "it"
      ? "Buongiorno, vorrei ricevere alcune informazioni sulla disponibilità e sulle tariffe di Rosa Blu Apartment."
      : "Hello, I would like to request some information about availability and rates for Rosa Blu Apartment.";
  const whatsappUrl = `${baseUrl}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#22c55e] px-4 py-2 text-xs font-semibold text-slate-900 shadow-lg hover:bg-[#16a34a]"
    >
      <MessageCircle className="h-4 w-4" />
      <span>{lang === "it" ? "Scrivici su WhatsApp" : "Chat on WhatsApp"}</span>
    </a>
  );
};

export const SiteHeader = () => {
  const { lang, setLang } = useLanguage();
  const nav = siteContent.navigation[lang];
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleNavClick = (item) => {
    const path = routeMap[item.id] || "/home";
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-white/40 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6 md:py-4">
        <button
          type="button"
          className="flex max-w-full items-center gap-2 cursor-pointer select-none text-sm font-semibold tracking-[0.18em] text-slate-800 md:text-base"
          onClick={() => navigate("/home")}
        >
          <img loading="lazy"
            src="/images/rosablu/logo/logo.png"
            alt="Logo Rosa Blu Apartment"
            className="h-12 w-auto md:h-16 drop-shadow-[0_0_6px_rgba(0,0,0,0.35)]"
          />
          <span>ROSA BLU APARTMENT</span>
        </button>

        {/* Nav desktop */}
        <nav className="hidden items-center gap-5 text-sm text-slate-700 md:flex">
          {nav.map((item) => {
            const path = routeMap[item.id] || "/home";
            const active = location.pathname === path;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavClick(item)}
                className={`whitespace-nowrap text-[0.68rem] uppercase tracking-[0.18em] transition-colors md:text-xs ${
                  active ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {/* Switch lingua desktop */}
          <div className="hidden items-center gap-1 rounded-full border border-slate-200 bg-white/70 p-1 text-xs font-medium text-slate-700 shadow-sm sm:flex">
            <button
              type="button"
              onClick={() => setLang("it")}
              className={`rounded-full px-3 py-1 transition-colors ${
                lang === "it" ? "bg-slate-900 text-white" : "hover:bg-slate-100"
              }`}
            >
              IT
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`rounded-full px-3 py-1 transition-colors ${
                lang === "en" ? "bg-slate-900 text-white" : "hover:bg-slate-100"
              }`}
            >
              EN
            </button>
          </div>

          {/* Pulsante Prenota desktop */}
          <Button
            variant="default"
            className="hidden rounded-full bg-[#b07157] px-4 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.15em] text-white shadow-md hover:bg-[#975b45] md:inline-flex md:text-xs"
            onClick={() => navigate("/contatti")}
          >
            {lang === "it" ? "Prenota" : "Book"}
          </Button>

          {/* Menu mobile (hamburger) */}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white/80 p-2 text-slate-800 shadow-sm md:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Overlay + tendina mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden">
          <div className="flex h-full items-start justify-end pr-3 pt-16">
            <div className="flex w-[78vw] max-w-xs flex-col rounded-3xl bg-gradient-to-b from-white/95 to-slate-100/95 px-4 py-6 shadow-2xl max-h-[80vh] overflow-y-auto animate-in slide-in-from-right duration-300">
              {/* Header pannello */}
              <div className="mb-4 flex items-center justify-between">
                <span className="text-xs font-semibold tracking-[0.2em] text-slate-700">
                  ROSA BLU APARTMENT
                </span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-full p-1 text-slate-600 hover:bg-slate-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Switch lingua mobile */}
              <div className="mb-4 flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 p-1 text-[0.7rem] font-medium text-slate-700">
                <button
                  type="button"
                  onClick={() => setLang("it")}
                  className={`flex-1 rounded-full px-3 py-1 text-center transition-colors ${
                    lang === "it"
                      ? "bg-slate-900 text-white"
                      : "hover:bg-slate-100"
                  }`}
                >
                  IT
                </button>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`flex-1 rounded-full px-3 py-1 text-center transition-colors ${
                    lang === "en"
                      ? "bg-slate-900 text-white"
                      : "hover:bg-slate-100"
                  }`}
                >
                  EN
                </button>
              </div>

              {/* Voci menu */}
              <div className="mt-3 flex flex-1 flex-col gap-2 overflow-y-auto text-sm">
                {nav.map((item) => {
                  const path = routeMap[item.id] || "/home";
                  const active = location.pathname === path;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        handleNavClick(item);
                        setMobileOpen(false);
                      }}
                      className={`w-full rounded-full px-3 py-2 text-left text-[0.68rem] uppercase tracking-[0.18em] transition-colors active:scale-95 ${
                        active
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-800 hover:bg-slate-200"
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}

                <Button
                  className="mt-3 w-full rounded-full bg-[#b07157] px-3 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-white transition-transform hover:bg-[#975b45] active:scale-95"
                  onClick={() => {
                    navigate("/contatti");
                    setMobileOpen(false);
                  }}
                >
                  {lang === "it"
                    ? "Richiedi disponibilità"
                    : "Request availability"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => {
  const { lang } = useLanguage();

  return (
    <footer className="border-t border-slate-200 bg-white py-5 text-xs text-slate-500">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-2 px-4 md:flex-row md:items-center md:px-6">
        <p>© {new Date().getFullYear()} Rosa Blu Apartment · Val di Sole</p>
        <p>
          {lang === "it"
            ? "Rosa Blu Apartment · Piano di Commezzadura, Val di Sole (TN)."
            : "Rosa Blu Apartment · Piano di Commezzadura, Val di Sole (Italy)."}
        </p>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <SiteHeader />
      <main className="bg-transparent overflow-x-hidden">{children}</main>
      <SiteFooter />
      <WhatsappBubble />
    </div>
  );
};
